<script setup lang="ts">
/**
 * THE ACCOUNTANT'S FILE: a month of takings as Pohoda imports them.
 *
 * Last whole month by default, because that is what gets closed; the running
 * month is there too, up to yesterday. A refusal says what to fix — a payment
 * form with no Pokladna is named in words, not as a code.
 */
import { computed, ref } from "vue";
import type { ConsoleApi } from "../api";
import { ApiError } from "../api";
import { exportMonths } from "../accounting-months";
import { downloadBlob } from "../csv";

const props = defineProps<{
  api: Pick<ConsoleApi, "pohodaExport">;
  /** The venue's trading day. */
  today: string;
  locale: string;
  t: (key: string) => string;
  /** Saves the file; the browser's download unless a test says otherwise. */
  download?: (name: string, blob: Blob) => void;
}>();

const months = computed(() => exportMonths(props.today));
const chosen = ref(months.value.find((m) => !m.partial)?.key ?? months.value[0]?.key ?? "");
const busy = ref(false);
const notice = ref<{ tone: "ok" | "bad"; text: string } | null>(null);

const label = (key: string, partial: boolean): string => {
  const name = new Intl.DateTimeFormat(props.locale, { month: "long", year: "numeric", timeZone: "UTC" }).format(
    new Date(`${key}-15T12:00:00Z`),
  );
  return partial ? `${name} (${props.t("pohoda.toYesterday")})` : name;
};

const REFUSALS: Readonly<Record<string, string>> = {
  "no-registration-id": "pohoda.noRegistrationId",
  "nothing-to-export": "pohoda.nothing",
  "day-not-closed": "pohoda.dayNotClosed",
  "pohoda-not-set-up": "pohoda.notSetUp",
  "training-venue": "pohoda.training",
};

const run = async (): Promise<void> => {
  const month = months.value.find((m) => m.key === chosen.value);
  if (!month || busy.value) return;
  busy.value = true;
  notice.value = null;
  try {
    const file = await props.api.pohodaExport(month.from, month.to);
    (props.download ?? downloadBlob)(`pohoda-${month.from}-${month.to}.xml`, file);
    notice.value = { tone: "ok", text: props.t("pohoda.downloaded") };
  } catch (e) {
    if (e instanceof ApiError && e.kind === "no-cash-account") {
      const forms = e.message.split(",").map((kind) => props.t(`tender.${kind}`));
      notice.value = { tone: "bad", text: `${props.t("pohoda.noCashAccount")} ${forms.join(", ")}. ${props.t("pohoda.fixInSettings")}` };
    } else {
      const key = e instanceof ApiError ? REFUSALS[e.kind] : undefined;
      notice.value = { tone: "bad", text: props.t(key ?? "common.failed") };
    }
  } finally {
    busy.value = false;
  }
};
</script>

<template>
  <section class="gf-panel pohoda">
    <div class="pohoda__text">
      <h2 class="gf-panel__title">{{ t("pohoda.heading") }}</h2>
      <p class="gf-muted pohoda__lead">{{ t("pohoda.lead") }}</p>
    </div>
    <div class="pohoda__row">
      <label class="gf-field">
        <span class="gf-label">{{ t("pohoda.month") }}</span>
        <select v-model="chosen" class="gf-input" data-field="month">
          <option v-for="m in months" :key="m.key" :value="m.key">{{ label(m.key, m.partial) }}</option>
        </select>
      </label>
      <button type="button" class="gf-button gf-button--primary" data-testid="pohoda-export" :disabled="busy" @click="run">
        {{ t("pohoda.download") }}
      </button>
    </div>
    <p v-if="notice" class="pohoda__notice" :class="`pohoda__notice--${notice.tone}`" :role="notice.tone === 'bad' ? 'alert' : 'status'">
      {{ notice.text }}
    </p>
  </section>
</template>

<style scoped>
.pohoda {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: var(--gf-space-2) var(--gf-space-4);
  align-items: end;
}

.pohoda__lead {
  margin: var(--gf-space-1) 0 0;
  font-size: var(--gf-text-sm);
  max-width: 48rem;
}

.pohoda__row {
  display: flex;
  align-items: flex-end;
  gap: var(--gf-space-2);
}

.pohoda__notice {
  grid-column: 1 / -1;
  margin: 0;
  font-size: var(--gf-text-sm);
}

.pohoda__notice--bad {
  color: var(--gf-color-danger);
}

@media (max-width: 720px) {
  .pohoda {
    grid-template-columns: 1fr;
  }
}
</style>
